import { useState } from 'react'
import type { Plant } from '../types'
import BloomCalendar from './BloomCalendar'

interface PlantCardProps {
  plant: Plant
  isSaved: boolean
  onSave: (id: string) => void
  onAddToContainer?: (plantId: string) => void
  compact?: boolean
}

const ROLE_COLORS: Record<string, string> = {
  thriller: 'bg-ac-purple text-ac-brown',
  filler: 'bg-ac-yellow text-ac-brown',
  spiller: 'bg-ac-blue text-ac-brown',
}

const WATER_LABELS: Record<string, string> = {
  low: 'Low water',
  moderate: 'Moderate water',
  high: 'High water',
}

const SUN_LABELS: Record<string, string> = {
  'full-sun': 'Full sun',
  'partial-shade': 'Part shade',
  'full-shade': 'Full shade',
}

const CARE_COLORS: Record<string, string> = {
  easy: 'text-green-600',
  moderate: 'text-yellow-600',
  difficult: 'text-red-500',
}

export default function PlantCard({ plant, isSaved, onSave, onAddToContainer, compact = false }: PlantCardProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="ac-card flex flex-col gap-2 cursor-pointer hover:shadow-ac hover:ring-2 hover:ring-ac-green/30 transition-all"
      style={{ borderLeftColor: plant.accentColor, borderLeftWidth: 6 }}
      onClick={() => setExpanded(e => !e)}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 shadow-ac-sm"
          style={{ backgroundColor: plant.accentColor + '40', border: `2px solid ${plant.accentColor}` }}
        >
          {plant.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-body font-extrabold text-ac-brown leading-tight truncate">{plant.name}</h3>
          <p className="font-body text-xs text-ac-brown-light italic truncate">{plant.scientificName}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {plant.role.map(role => (
              <span key={role} className={`ac-badge ${ROLE_COLORS[role]} text-[10px] capitalize`}>{role}</span>
            ))}
            {plant.toxicity.pets && (
              <span className="ac-badge text-[10px] bg-red-100 text-red-700 border-red-300">Toxic to pets</span>
            )}
            {plant.toxicity.children && (
              <span className="ac-badge text-[10px] bg-orange-100 text-orange-700 border-orange-300">Toxic to kids</span>
            )}
            {plant.nativeRegions.map(region => (
              <span key={region} className="ac-badge text-[10px] bg-green-100 text-green-700 border-green-300">{region} native</span>
            ))}
            {plant.isPerennial && (
              <span className="ac-badge text-[10px] bg-blue-100 text-blue-700 border-blue-300">Perennial</span>
            )}
            {plant.reviewStatus === 'partial' && (
              <span className="ac-badge text-[10px] bg-amber-100 text-amber-800 border-amber-300">Source pass</span>
            )}
          </div>
        </div>
        <button
          className="text-xl shrink-0 hover:scale-125 transition-transform"
          onClick={event => { event.stopPropagation(); onSave(plant.id) }}
          title={isSaved ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          {isSaved ? '*' : '+'}
        </button>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs font-body text-ac-brown-light">
        <span>{SUN_LABELS[plant.sunlight[0]]}{plant.sunlight.length > 1 ? ` +${plant.sunlight.length - 1}` : ''}</span>
        <span>{WATER_LABELS[plant.water]}</span>
        <span className={`font-bold ${CARE_COLORS[plant.careLevel]} capitalize`}>Care: {plant.careLevel}</span>
        <span>Zone {plant.hardiness.min}-{plant.hardiness.max}</span>
      </div>

      {!compact && (
        <div onClick={event => event.stopPropagation()}>
          <BloomCalendar bloomMonths={plant.bloomMonths} />
        </div>
      )}

      {expanded && (
        <div className="border-t-2 border-dashed border-ac-bg pt-2 mt-1 space-y-2" onClick={event => event.stopPropagation()}>
          <p className="font-body text-sm text-ac-brown">{plant.description}</p>

          {plant.reviewStatus === 'partial' && (
            <p className="font-body text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
              This plant has an initial source pass, but final taxonomy, toxicity, or container guidance may still be under review.
            </p>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs font-body">
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Sunlight</p>
              <p className="text-ac-brown-light">{plant.sunlight.map(sun => SUN_LABELS[sun]).join(', ')}</p>
            </div>
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Watering</p>
              <p className="text-ac-brown-light">{plant.wateringFrequency}</p>
            </div>
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Fertilizing</p>
              <p className="text-ac-brown-light">{plant.fertilizingFrequency}</p>
            </div>
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Wind</p>
              <p className="text-ac-brown-light capitalize">{plant.windTolerance} tolerance</p>
            </div>
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Min. pot size</p>
              <p className="text-ac-brown-light capitalize">{plant.minContainerSize}</p>
            </div>
            <div className="bg-ac-bg rounded-lg p-2">
              <p className="font-bold text-ac-brown">Pest resistance</p>
              <p className="text-ac-brown-light capitalize">{plant.pestResistance}</p>
            </div>
          </div>

          <div className="bg-ac-bg rounded-lg p-2 text-xs font-body">
            <p className="font-bold text-ac-brown">Overwintering</p>
            <p className="text-ac-brown-light">{plant.overwinteringNote}</p>
          </div>

          {(plant.ecologicalBenefits?.length || plant.crowdingGuidance || plant.depthGuidance) && (
            <div className="bg-ac-bg rounded-lg p-2 text-xs font-body space-y-1">
              {plant.ecologicalBenefits?.length ? (
                <>
                  <p className="font-bold text-ac-brown">Ecological benefit</p>
                  <p className="text-ac-brown-light">{plant.ecologicalBenefits.join(', ')}</p>
                </>
              ) : null}
              {plant.crowdingGuidance && <p className="text-ac-brown-light">{plant.crowdingGuidance}</p>}
              {plant.depthGuidance && <p className="text-ac-brown-light">{plant.depthGuidance}</p>}
            </div>
          )}

          {onAddToContainer && (
            <button
              className="ac-btn-green w-full text-sm"
              onClick={() => onAddToContainer(plant.id)}
            >
              + Add to a Container
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-center gap-1 pt-1 border-t border-dashed border-ac-bg text-xs text-ac-brown-light font-body select-none">
        <span>{expanded ? 'Less' : 'Details'}</span>
        <span className={`transition-transform duration-200 text-sm ${expanded ? 'rotate-180' : ''}`}>v</span>
      </div>
    </div>
  )
}
